import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import { CheckRounded as CheckIcon } from "@mui/icons-material";
import api from "../api";

const GRADIENT = "linear-gradient(135deg, #4450A5 0%, #EF2A1E 100%)";

function hiddenKey(role) {
  try {
    const id = JSON.parse(localStorage.getItem("userData") || "{}")?.id;
    return `gettingStartedHidden:${role}:${id ?? "unknown"}`;
  } catch {
    return `gettingStartedHidden:${role}:unknown`;
  }
}

function readHidden(role) {
  try {
    return localStorage.getItem(hiddenKey(role)) === "1";
  } catch {
    return false;
  }
}

/** Wording and destination for each step key the API sends (GettingStartedService). */
function stepCopy(key, { prefix, child }) {
  const childName = child?.first_name || "Your child";
  const childLogin = child?.username || child?.email;
  switch (key) {
    case "profile":
      return {
        title: "Tell us your year and target schools",
        line: "Papers and mock exams for your schools come first.",
        cta: "Add details",
        to: `/${prefix}/profile`,
      };
    case "access":
      return {
        title: "Start your free trial, or pick a paper",
        line: "7 days free, no card. Or buy just the papers you want.",
        cta: "See options",
        to: `/${prefix}/choose-plan?plans=1`,
      };
    case "first_practice":
      return {
        title: "Do your first practice",
        line: "A short practice paper or worksheet.",
        cta: "Start first practice",
        to: `/${prefix}/past-papers`,
      };
    case "first_result":
      return {
        title: "See your results",
        line: "Your score, and which topics to practise next.",
        cta: "See results",
        to: `/${prefix}/mock-exams?tab=exam`,
      };
    case "child_account":
      return {
        title: "Check your child's account",
        line: childLogin
          ? `${childName} logs in with their own login: ${childLogin}.`
          : "Add your child so they get their own login.",
        cta: "Add your child",
        to: `/${prefix}/add-student`,
      };
    case "security":
      return {
        title: "Change your password and parent PIN",
        line: "We emailed you starter ones. The PIN unlocks the parent view on your child's account.",
        cta: "Change now",
        to: `/${prefix}/settings`,
      };
    case "child_access":
      return {
        title: "Give your child their first practice",
        line: "Start the free trial for them, or buy and assign a paper.",
        cta: "Assign a paper",
        to: `/${prefix}/my-purchases`,
      };
    case "child_result":
      return {
        title: "See your child's results",
        line: "Scores, weak topics and what to practise next.",
        cta: "See results",
        to: `/${prefix}/mock-exam-results`,
      };
    default:
      return null;
  }
}

/**
 * "Getting started" on top of the student and parent portal dashboards: the same four steps as
 * My Learning on the public site, worked out by GET {role}/getting-started so they tick
 * themselves off. Only the next step has a button. Hidden at 4 of 4, or by "Hide".
 */
export default function GettingStartedCard({ role = "student" }) {
  const navigate = useNavigate();
  const prefix =
    role === "parent"
      ? process.env.REACT_APP_PARENT_PREFIX || "parent"
      : process.env.REACT_APP_STUDENT_PREFIX || "student";
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(() => readHidden(role));

  const load = useCallback(() => {
    api
      .get(`${role}/getting-started`)
      .then((res) => setData(res.data?.data || null))
      .catch(() => setData(null));
  }, [role]);

  useEffect(() => {
    if (!hidden) load();
  }, [hidden, load]);

  if (hidden || !data || !Array.isArray(data.steps)) return null;
  const total = data.total || data.steps.length;
  if (data.done_count >= total) return null;

  const hide = () => {
    try {
      localStorage.setItem(hiddenKey(role), "1");
    } catch {
      /* private mode: hide for this visit only */
    }
    setHidden(true);
  };
  const parent = role === "parent";

  return (
    <Box
      component="section"
      aria-label="Getting started"
      sx={{
        bgcolor: "#fff",
        border: "1px solid #E6E8F2",
        borderRadius: "20px",
        p: { xs: 2.25, md: "22px 26px" },
        mb: 3,
        boxShadow: "0 4px 18px rgba(26,31,54,0.05)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: { xs: 17, md: 19 }, fontWeight: 800, color: "#1A1F36" }}>
            {parent ? "Getting started as a parent" : "Getting started"}
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: "#4A5168", mt: 0.25 }}>
            {parent
              ? "Set up your child, then follow their progress."
              : "Four steps to your first result. They tick themselves off."}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#2B3485", whiteSpace: "nowrap" }}>
          {data.done_count} of {total}
        </Typography>
        <Button onClick={hide} size="small" sx={{ textTransform: "none", fontWeight: 600, color: "#6B7280", minWidth: 0 }}>
          Hide
        </Button>
      </Box>
      <Box
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={data.done_count}
        sx={{ height: 8, borderRadius: 4, bgcolor: "#EEF0F6", mt: 1.75, mb: 0.75, overflow: "hidden" }}
      >
        <Box sx={{ width: `${(data.done_count / total) * 100}%`, height: 8, bgcolor: "#16A34A" }} />
      </Box>

      {data.steps.map((step, index) => {
        const copy = stepCopy(step.key, { prefix, child: data.child });
        if (!copy) return null;
        const isNext = step.key === data.next;
        return (
          <Box
            key={step.key}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              gap: { xs: 1.25, sm: 2 },
              py: 1.6,
              px: { sm: 0.5 },
              borderBottom: index === data.steps.length - 1 ? "none" : "1px solid #EEF0F6",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.75, flex: 1, minWidth: 0 }}>
              <Box
                aria-hidden="true"
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 800,
                  fontSize: 14,
                  boxSizing: "border-box",
                  ...(step.done
                    ? { bgcolor: "#16A34A", color: "#fff" }
                    : isNext
                    ? { bgcolor: "#4450A5", color: "#fff" }
                    : { border: "2px solid #D5D9EA", color: "#6B7280" }),
                }}
              >
                {step.done ? <CheckIcon sx={{ fontSize: 18 }} /> : index + 1}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 15,
                    fontWeight: step.done ? 600 : isNext ? 800 : 700,
                    color: step.done ? "#6B7280" : "#1A1F36",
                    textDecoration: step.done ? "line-through" : "none",
                  }}
                >
                  {copy.title}
                </Typography>
                <Typography sx={{ fontSize: 13.5, color: "#4A5168", mt: 0.25 }}>{copy.line}</Typography>
              </Box>
            </Box>
            {step.done ? (
              <Typography sx={{ display: { xs: "none", sm: "block" }, fontSize: 13, fontWeight: 700, color: "#15803D" }}>
                Done
              </Typography>
            ) : isNext ? (
              <Button
                onClick={() => navigate(copy.to)}
                variant="contained"
                sx={{
                  minHeight: 44,
                  px: 2.25,
                  borderRadius: "10px",
                  background: GRADIENT,
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 14,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  boxShadow: "none",
                  ml: { xs: 6, sm: 0 },
                  "&:hover": { background: "linear-gradient(135deg, #3b4694 0%, #d9261b 100%)", boxShadow: "none" },
                }}
              >
                {copy.cta}
              </Button>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}
